import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, X, Github, GitFork, Users, Waves } from 'lucide-react';
import { SectionTitle } from '../../components/ui/PageLayout';

function dismissKey(orgId) {
  return `vp_onboarding_dismissed_${orgId}`;
}

function StepCard({ done, title, description, actionLabel, actionTo, onAction }: any) {
  const inner = (
    <>
      <div className="flex items-start gap-3">
        <span
          className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full border-2 ${
            done
              ? 'border-green-400 bg-green-900/30 text-green-300'
              : 'border-dark-border bg-dark-surface text-slate-400'
          }`}
          aria-hidden
        >
          {done ? <Check className="h-4 w-4" /> : null}
        </span>
        <div className="min-w-0 flex-1">
          <h3 className="text-sm font-semibold text-slate-100">{title}</h3>
          <p className="mt-1 text-sm text-slate-400">{description}</p>
          {!done && actionTo ? (
            <Link
              to={actionTo}
              className="btn-primary-mdl mt-3 inline-flex items-center gap-2 text-sm"
            >
              {actionLabel}
            </Link>
          ) : null}
          {!done && onAction ? (
            <button
              type="button"
              className="btn-primary-mdl mt-3 inline-flex items-center gap-2 text-sm"
              onClick={onAction}
            >
              {actionLabel}
            </button>
          ) : null}
        </div>
      </div>
    </>
  );
  return <div className="card-mdl-static p-4 h-full">{inner}</div>;
}

export default function DashboardGettingStarted({ orgId, stats, hasGitHubIntegration, onDismiss }: any) {
  const [dismissed, setDismissed] = useState(() => {
    if (!orgId) return false;
    try {
      return localStorage.getItem(dismissKey(orgId)) === '1';
    } catch {
      return false;
    }
  });

  const steps = useMemo(() => {
    const repoCount = stats?.repositoryCount ?? 0;
    const streamCount = stats?.streamCount ?? 0;
    const projectCount = stats?.projectCount ?? 0;
    const memberCount = stats?.memberCount ?? 0;
    return {
      github: Boolean(hasGitHubIntegration),
      repos: repoCount > 0,
      team: memberCount > 1,
      structure: streamCount > 0 || projectCount > 0,
    };
  }, [stats, hasGitHubIntegration]);

  const allComplete = steps.github && steps.repos && steps.team && steps.structure;
  const isZeroState = !steps.repos && !steps.structure;

  if (!orgId || dismissed || !isZeroState) return null;
  if (allComplete) return null;

  const handleDismiss = () => {
    try {
      localStorage.setItem(dismissKey(orgId), '1');
    } catch {
      /* ignore */
    }
    setDismissed(true);
    onDismiss?.();
  };

  return (
    <section className="mb-8" aria-labelledby="getting-started-heading">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div>
          <SectionTitle id="getting-started-heading" className="mb-0">
            Getting started
          </SectionTitle>
          <p className="mt-1 text-sm text-slate-400">
            Complete these steps to unlock GitHub analytics and team workflows.
          </p>
        </div>
        <button
          type="button"
          onClick={handleDismiss}
          className="btn-secondary-mdl inline-flex items-center gap-1.5 text-sm"
        >
          <X className="h-4 w-4" aria-hidden />
          Dismiss
        </button>
      </div>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StepCard
          done={steps.github}
          title="Connect your GitHub organisation"
          description="Link GitHub so VantagePoint can discover repositories and sync commit data."
          actionLabel="Open GitHub settings"
          actionTo="/settings/organisation/github"
        />
        <StepCard
          done={steps.repos}
          title="Add your repositories"
          description="Connect repositories to a project so metrics and dashboards populate."
          actionLabel="View repositories"
          actionTo="/repositories"
        />
        <StepCard
          done={steps.team}
          title="Invite your team members"
          description="Bring colleagues into your organisation so everyone sees the same streams and projects."
          actionLabel="Manage members"
          actionTo="/members"
        />
        <StepCard
          done={steps.structure}
          title="Create your first stream or project"
          description="Organise work into streams and projects to group repositories and reporting."
          actionLabel="View streams"
          actionTo="/streams"
        />
      </div>
      <div className="mt-3 flex flex-wrap gap-4 text-xs text-slate-400">
        <span className="inline-flex items-center gap-1">
          <Github className="h-3.5 w-3.5" aria-hidden /> GitHub
        </span>
        <span className="inline-flex items-center gap-1">
          <GitFork className="h-3.5 w-3.5" aria-hidden /> Repositories
        </span>
        <span className="inline-flex items-center gap-1">
          <Users className="h-3.5 w-3.5" aria-hidden /> Members
        </span>
        <span className="inline-flex items-center gap-1">
          <Waves className="h-3.5 w-3.5" aria-hidden /> Streams
        </span>
      </div>
    </section>
  );
}
